import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import client from "@/app/client";
import { Sun, Heart, Wind, CloudRain, Moon, Check } from "lucide-react";

const MOODS = [
  { key: "open", label: "Open", icon: Sun },
  { key: "tender", label: "Tender", icon: Heart },
  { key: "restless", label: "Restless", icon: Wind },
  { key: "heavy", label: "Heavy", icon: CloudRain },
  { key: "quiet", label: "Quiet", icon: Moon },
];

const MOOD_LABEL = Object.fromEntries(MOODS.map((m) => [m.key, m.label]));

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function Journal() {
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [reflection, setReflection] = useState(null);
  const [entries, setEntries] = useState([]);

  const load = () =>
    client
      .get("/journal")
      .then((r) => setEntries(r.data.entries || []))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!selected || loading) return;
    setLoading(true);
    try {
      const { data } = await client.post("/journal", { mood: selected, note: note || null });
      setReflection(data.reflection);
      setSelected(null);
      setNote("");
      load();
    } catch (e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="screen-journal" className="space-y-10">
      <div>
        <div className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/55 font-body">
          Mood journal
        </div>
        <h1 className="mt-3 font-display tracking-tight leading-[1.02] text-[clamp(2rem,5vw,3rem)] font-medium">
          How are you <span className="italic font-light text-aurora">arriving</span>?
        </h1>
      </div>

      <div className="border border-[#F4F1EC]/12 bg-[#F4F1EC]/[0.03] p-6 md:p-8">
        <div className="grid grid-cols-5 gap-2 max-w-md">
          {MOODS.map((m) => {
            const Icon = m.icon;
            const on = selected === m.key;
            return (
              <button
                key={m.key}
                data-testid={`journal-mood-${m.key}`}
                onClick={() => setSelected(m.key)}
                className={`flex flex-col items-center gap-2 py-3 border transition-colors ${
                  on
                    ? "border-[#FFB7C6] bg-[#FFB7C6]/10 text-[#FFB7C6]"
                    : "border-[#F4F1EC]/12 text-[#F4F1EC]/65 hover:border-[#F4F1EC]/30"
                }`}
              >
                <Icon size={18} strokeWidth={1.5} />
                <span className="text-[9px] uppercase tracking-[0.14em] font-body">{m.label}</span>
              </button>
            );
          })}
        </div>

        <textarea
          data-testid="journal-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="A line, if you'd like. What's moving through you?"
          rows={2}
          className="mt-5 w-full bg-transparent border-b border-[#F4F1EC]/20 text-[#F4F1EC] placeholder:text-[#F4F1EC]/35 font-body text-sm py-2 outline-none resize-none focus:border-[#FFB7C6] transition-colors"
        />

        <button
          data-testid="journal-submit"
          onClick={submit}
          disabled={!selected || loading}
          className="mt-6 h-11 px-7 font-body text-[11px] uppercase tracking-[0.24em] bg-[#F4F1EC] text-[#070608] hover:bg-white disabled:opacity-40 transition-colors"
        >
          {loading ? "…" : "Save check-in"}
        </button>

        <AnimatePresence>
          {reflection && (
            <motion.div
              data-testid="journal-reflection"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-6 flex items-start gap-3"
            >
              <span className="h-7 w-7 mt-0.5 inline-flex items-center justify-center bg-gradient-to-br from-[#FFB7C6] to-[#A8E6FF] text-[#070608] shrink-0">
                <Check size={14} strokeWidth={2.2} />
              </span>
              <p className="font-display text-[20px] italic font-light leading-[1.4] text-[#F4F1EC]">
                {reflection}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#F4F1EC]/50 font-body mb-4">
          Your history
        </div>
        {entries.length === 0 ? (
          <p className="font-body text-sm text-[#F4F1EC]/45">
            No check-ins yet. Your first one will appear here.
          </p>
        ) : (
          <div className="space-y-px">
            {entries.map((e) => (
              <div
                key={e.id}
                data-testid="journal-entry"
                className="flex items-start gap-5 py-5 border-b border-[#F4F1EC]/10"
              >
                <div className="w-28 shrink-0">
                  <div className="font-display text-[20px] text-aurora">{MOOD_LABEL[e.mood] || e.mood}</div>
                  <div className="font-body text-[11px] text-[#F4F1EC]/40 mt-1">{formatDate(e.created_at)}</div>
                </div>
                <div className="flex-1">
                  {e.note && <p className="font-body text-sm text-[#F4F1EC]/80">"{e.note}"</p>}
                  <p className="font-body text-sm text-[#F4F1EC]/55 mt-1 leading-relaxed">{e.reflection}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
